export interface Folder {
  /**
   * Folder ID. Generated when a folder is created in the Media Manager.
   * @maxLength 100
   */
  id?: string;
  /**
   * Folder name as it appears in the Media Manager.
   * @maxLength 1000
   */
  displayName?: string;
  /**
   * ID of the folder's parent folder. <br /> Default: `media-root` folder.
   * @maxLength 100
   */
  parentFolderId?: string;
  /**
   * Date the folder was created.
   * @readonly
   */
  createdDate?: Date | null;
  /**
   * Date the folder was updated.
   * @readonly
   */
  updatedDate?: Date | null;
  /**
   * State of the folder.
   * @readonly
   */
  state?: StateWithLiterals;
  /**
   * Namespace of the folder.
   * @internal
   */
  namespace?: NamespaceWithLiterals;
  /**
   * Site ID the folder belongs to.
   * @readonly
   * @maxLength 100
   */
  siteId?: string;
}

export enum State {
  OK = 'OK',
  DELETED = 'DELETED',
}

/** @enumType */
export type StateWithLiterals = State | 'OK' | 'DELETED';

export enum Namespace {
  /** No namespaces */
  NO_NAMESPACE = 'NO_NAMESPACE',
  /** Custom namespace */
  OTHERS = 'OTHERS',
  /** Wix videos */
  WIX_VIDEO = 'WIX_VIDEO',
  /** Wix music */
  WIX_MUSIC = 'WIX_MUSIC',
  /** Albums and art store */
  ALBUMS_AND_ART_STORE = 'ALBUMS_AND_ART_STORE',
  /** Wix ecom */
  WIX_ECOM = 'WIX_ECOM',
  /** Photo share */
  PHOTO_SHARE_APP = 'PHOTO_SHARE_APP',
  /** Sharing app */
  SHARING_APP = 'SHARING_APP',
  /** Chat (engage) */
  CHAT = 'CHAT',
  /** Logo builder */
  LOGO_BUILDER = 'LOGO_BUILDER',
  /** Albums old namespace */
  ALBUMS_OLD = 'ALBUMS_OLD',
  /** Mobile chat */
  CHAT_MOBILE = 'CHAT_MOBILE',
  /** Wix Forms */
  WIX_FORMS = 'WIX_FORMS',
  /**
   * Draft - files that will be deleted if not published
   * @internal
   */
  DRAFTS = 'DRAFTS',
  /** Wix legends */
  WIX_LEGENDS = 'WIX_LEGENDS',
  /**
   * Restricted Data
   * @internal
   */
  DATA_RESTRICTED = 'DATA_RESTRICTED',
  /** Portfolio */
  PORTFOLIO = 'PORTFOLIO',
  /** LOGOS */
  LOGOS = 'LOGOS',
}

/** @enumType */
export type NamespaceWithLiterals =
  | Namespace
  | 'NO_NAMESPACE'
  | 'OTHERS'
  | 'WIX_VIDEO'
  | 'WIX_MUSIC'
  | 'ALBUMS_AND_ART_STORE'
  | 'WIX_ECOM'
  | 'PHOTO_SHARE_APP'
  | 'SHARING_APP'
  | 'CHAT'
  | 'LOGO_BUILDER'
  | 'ALBUMS_OLD'
  | 'CHAT_MOBILE'
  | 'WIX_FORMS'
  | 'WIX_LEGENDS'
  | 'PORTFOLIO'
  | 'LOGOS';

export interface CreateFolderRequest {
  /**
   * Folder name that appears in the Media Manager.
   * @maxLength 1000
   */
  displayName: string;
  /**
   * ID of the folder's parent folder.
   * @maxLength 100
   */
  parentFolderId?: string | null;
  /**
   * The namespace this folder will be created under
   * @internal
   * @maxLength 100
   */
  rawNamespace?: string | null;
  /**
   * The namespace this folder will be created under
   * Note - Namespace value `Others` is not supported and will produce an error
   * @internal
   */
  namespace?: NamespaceWithLiterals;
}

export interface CreateFolderResponse {
  /** Information about the newly created folder. */
  folder?: Folder;
}

export interface GetFolderRequest {
  /**
   * Folder ID.
   * @maxLength 100
   */
  folderId: string;
}

export interface GetFolderResponse {
  /** Information about the folder. */
  folder?: Folder;
}

export interface ListFoldersRequest {
  /**
   * ID of the folder's parent folder.
   *
   * Default: `media-root` folder.
   * @maxLength 100
   */
  parentFolderId?: string | null;
  /**
   * Field name and order to sort by. One of:
   *
   * `displayName`
   * `updatedDate`
   *
   * Default: `updatedDate` in `desc` order.
   */
  sort?: CommonSorting;
  /** Cursor and paging information. */
  paging?: CursorPaging;
  /**
   * List folders of the specified namespace.
   * @internal
   * @maxLength 100
   */
  rawNamespace?: string | null;
  /**
   * List folders of the specified namespace.
   * Note - Namespace value `Others` is not supported and will produce an error
   * @internal
   */
  namespace?: NamespaceWithLiterals;
}

export interface CommonSorting {
  /**
   * Name of the field to sort by.
   * @maxLength 512
   */
  fieldName?: string;
  /** Sort order. */
  order?: CommonSortOrderWithLiterals;
  /**
   * When `field_name` is a property of repeated field that is marked as `MATCH_ITEMS` and sort should be done by
   * a specific element from a collection, filter can/should be provided to ensure correct sort value is picked.
   *
   * If multiple filters are provided, they are combined with AND operator.
   *
   * Example:
   * Given we have document like {"id": "1", "nestedField": [{"price": 10, "region": "EU"}, {"price": 20, "region": "US"}]}
   * and `nestedField` is marked as `MATCH_ITEMS`, to ensure that sorting is done by correct region, filter should be
   * { fieldName: "nestedField.price", "select_items_by": [{"nestedField.region": "US"}] }
   * @internal
   * @maxSize 10
   */
  selectItemsBy?: Record<string, any>[] | null;
  /**
   * Origin point for geo-distance sorting on a GEO field
   * results are ordered by distance from this point (ASC = nearest first, DESC = farthest first).
   */
  origin?: AddressLocation;
}

export enum CommonSortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

/** @enumType */
export type CommonSortOrderWithLiterals = CommonSortOrder | 'ASC' | 'DESC';

export interface AddressLocation {
  /** Address latitude. */
  latitude?: number | null;
  /** Address longitude. */
  longitude?: number | null;
}

export interface CursorPaging {
  /**
   * Maximum number of items to return in the results.
   * @max 100
   */
  limit?: number | null;
  /**
   * Pointer to the next or previous page in the list of results.
   *
   * Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
   * Not relevant for the first request.
   * @maxLength 16000
   */
  cursor?: string | null;
}

export interface ListFoldersResponse {
  /**
   * Information about the folders in the requested folder.
   * @maxSize 200
   */
  folders?: Folder[];
  /** The next cursor if it exists. */
  nextCursor?: PagingMetadataV2;
}

export interface PagingMetadataV2 {
  /** Total number of items that match the query. Returned if offset paging is used and the `tooManyToCount` flag is not set. */
  total?: number | null;
  /** Cursors to navigate through the result pages using `next` and `prev`. Returned if cursor paging is used. */
  cursors?: Cursors;
  /**
   * Indicates if there are more results after the current page.
   * If `true`, another page of results can be retrieved.
   * If `false`, this is the last page.
   * @internal
   */
  hasNext?: boolean | null;
}

export interface Cursors {
  /**
   * Cursor string pointing to the next page in the list of results.
   * @maxLength 16000
   */
  next?: string | null;
}

export interface SearchFoldersRequest {
  /**
   * The root folder in the Media Manager to search in.
   * For more information, see Root Folders in [Terminology](https://dev.wix.com/docs/rest/assets/media/media-manager/introduction#terminology).
   *
   *
   * Default: `MEDIA_ROOT`.
   */
  rootFolder?: RootFolderWithLiterals;
  /**
   * Field name and order to sort by. One of:
   *
   * `displayName`
   * `updatedDate`
   *
   * Default: `updatedDate` in `desc` order.
   */
  sort?: CommonSorting;
  /** Cursor and paging information. */
  paging?: CursorPaging;
  /**
   * Free text to match in searchable fields.
   *
   * For example, search for specific values in a folder's `displayName`.
   * @maxLength 200
   */
  search?: string | null;
  /**
   * Search folders of the specified namespace.
   * @internal
   * @maxLength 100
   */
  rawNamespace?: string | null;
  /**
   * Search folders of the specified namespace.
   * Note - Namespace value `Others` is not supported and will produce an error
   * @internal
   */
  namespace?: NamespaceWithLiterals;
  /**
   * Optional target site ID to search folders on a different site than the caller's.
   * When provided, the caller must have MEDIA.SITE_MEDIA_FILES_READ permission on the target site.
   * @internal
   * @maxLength 100
   */
  siteId?: string | null;
}

export enum RootFolder {
  /** Contains all files and folders in the Media Manager's **Site Files** tab in the UI. */
  MEDIA_ROOT = 'MEDIA_ROOT',
  /** Contains all files and folders in the Media Manager's **Trash** tab in the UI. */
  TRASH_ROOT = 'TRASH_ROOT',
  /** Contains all files and folders created by site visitors or members. */
  VISITOR_UPLOADS_ROOT = 'VISITOR_UPLOADS_ROOT',
}

/** @enumType */
export type RootFolderWithLiterals =
  | RootFolder
  | 'MEDIA_ROOT'
  | 'TRASH_ROOT'
  | 'VISITOR_UPLOADS_ROOT';

export interface SearchFoldersResponse {
  /**
   * Retrieved folders that match the search criteria specified in the request.
   *
   * Each folder includes all standard folder information.
   * @maxSize 200
   */
  folders?: Folder[];
  /** The next cursor if it exists. */
  nextCursor?: PagingMetadataV2;
}

export interface UpdateFolderRequest {
  /** Folder to update. */
  folder: Folder;
  /**
   * Set of fields to update. Fields that aren't included in `fieldMask.paths` are ignored.
   * @internal
   */
  fieldMask?: string[];
}

export interface UpdateFolderResponse {
  /** Information about the updated folder. */
  folder?: Folder;
}

export interface UnsupportedRequestValueError {
  /**
   * Optional A list of allowed values
   * @maxSize 100
   * @maxLength 200
   */
  allowedValues?: string[];
  /**
   * The unsupported value send in the request
   * @maxLength 200
   */
  requestValue?: string;
}

export interface GenerateFolderDownloadUrlRequest {
  /**
   * Folder ID.
   * @maxLength 100
   */
  folderId: string;
}

export interface GenerateFolderDownloadUrlResponse {
  /**
   * URL for downloading a specific folder in the Media Manager.
   * @format WEB_URL
   */
  downloadUrl?: string;
}

export interface BulkDeleteFoldersRequest {
  /**
   * IDs of the folders to move to the Media Manager's trash bin.
   * @maxSize 100
   * @maxLength 100
   */
  folderIds: string[];
  /**
   * Whether the specified folders are permanently deleted. <br />
   * Default: `false`
   */
  permanent?: boolean;
}

export interface BulkDeleteFoldersResponse {}

export interface BulkRestoreFoldersFromTrashBinRequest {
  /**
   * IDs of the folders to restore from the Media Manager's trash bin.
   * @maxSize 100
   * @maxLength 100
   */
  folderIds: string[];
}

export interface BulkRestoreFoldersFromTrashBinResponse {}

export interface ListDeletedFoldersRequest {
  /**
   * ID of the folder's parent folder.
   * @maxLength 100
   */
  parentFolderId?: string | null;
  /**
   * Field name and order to sort by. One of:
   * * `displayName`
   * * `updatedDate`
   * Default: `updatedDate` in `desc` order.
   */
  sort?: CommonSorting;
  /** Cursor and paging information. */
  paging?: CursorPaging;
  /**
   * List folders of the specified namespace.
   * @internal
   * @maxLength 100
   */
  rawNamespace?: string | null;
  /**
   * List folders of the specified namespace.
   * Note - Namespace value `Others` is not supported and will produce an error
   * @internal
   */
  namespace?: NamespaceWithLiterals;
}

export interface ListDeletedFoldersResponse {
  /**
   * List of folders in the Media Manager's trash bin.
   * @maxSize 200
   */
  folders?: Folder[];
  /** The next cursor if it exists. */
  nextCursor?: PagingMetadataV2;
}

export interface QueryFoldersRequest {
  /**
   * Query options, including filter, sort, and cursor paging.
   *
   * See [API Query Language](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-the-wix-api-query-language) and
   * [Sorting and Paging](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-sorting-and-paging) for more information.
   */
  query?: CursorQuery;
}

export interface CursorQuery extends CursorQueryPagingMethodOneOf {
  /**
   * Cursor paging options.
   *
   * Learn more about [cursor paging](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#cursor-paging).
   */
  cursorPaging?: CursorPaging;
  /**
   * Filter object.
   *
   * Learn more about [filtering](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#filters).
   */
  filter?: Record<string, any> | null;
  /**
   * Sort object.
   *
   * Learn more about [sorting](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#sorting).
   * @maxSize 1
   */
  sort?: Sorting[];
}

/** @oneof */
export interface CursorQueryPagingMethodOneOf {
  /**
   * Cursor paging options.
   *
   * Learn more about [cursor paging](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#cursor-paging).
   */
  cursorPaging?: CursorPaging;
}

export interface Sorting {
  /**
   * Name of the field to sort by.
   * @maxLength 512
   */
  fieldName?: string;
  /**
   * Sort order. Use `ASC` for ascending order or `DESC` for descending order.
   *
   * Default: `ASC`
   */
  order?: SortOrderWithLiterals;
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface QueryFoldersResponse {
  /**
   * Retrieved folders that match the query.
   * @maxSize 200
   */
  folders?: Folder[];
  /** Paging metadata, including the cursors for the next and previous pages. */
  pagingMetadata?: PagingMetadataV2;
}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at `16:00` and then again at `16:01`, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entityAsJson?: string;
  /**
   * Indicates the event was triggered by a restore-from-trashbin operation for a previously deleted entity
   * @internal
   */
  triggeredByUndelete?: boolean | null;
  /** Indicates the event was triggered by a restore-from-trashbin operation for a previously deleted entity */
  restoreInfo?: RestoreInfo;
  /**
   * Optional domain-specific metadata defined by the API owner, encoded as JSON.
   * @internal
   */
  additionalMetadataAsJson?: string | null;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntityAsJson?: string;
  /**
   * This field is currently part of the of the EntityUpdatedEvent msg, but scala/node libraries which implements the domain events standard
   * wont populate it / have any reference to it in the API.
   * The main reason for it is that fetching the old entity from the DB will have a performance hit on an update operation so unless truly needed,
   * the developer should send only the new (current) entity.
   * An additional reason is not wanting to send this additional entity over the wire (kafka) since in some cases it can be really big
   * Developers that must reflect the old entity will have to implement their own domain event sender mechanism which will follow the DomainEvent proto message.
   * @internal
   * @deprecated
   */
  previousEntityAsJson?: string | null;
  /**
   * Map of fully qualified field paths to their previous values for fields that changed.
   * For more details please see [AIP](https://dev.wix.com/docs/rnd-general/articles/p13n-guidelines-aips/guidance-aips/design-patterns/7016-events#modified-fields-format)
   * @internal
   */
  modifiedFields?: Record<string, any>;
  /**
   * Optional domain-specific metadata defined by the API owner, encoded as JSON.
   * @internal
   */
  additionalMetadataAsJson?: string | null;
}

export interface EntityDeletedEvent {
  /**
   * Indicates if the entity is sent to trash-bin. only available when trash-bin is enabled
   * @internal
   */
  movedToTrash?: boolean | null;
  /** Entity that was deleted. */
  deletedEntityAsJson?: string | null;
  /**
   * Optional domain-specific metadata defined by the API owner, encoded as JSON.
   * @internal
   */
  additionalMetadataAsJson?: string | null;
}

export interface ActionEvent {
  bodyAsJson?: string;
}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

/** @docsIgnore */
export type UpdateFolderValidationErrors = {
  ruleName?: 'FIELD_MASK_IS_EMPTY_OR_MISSING';
};
